import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid";
import TableFilters from "../tmt/TableFilters";
import TableActionButtons from "../tmt/TableActionButtons";
import CustomSnackbar from "../common/CustomSnackbar";
import { DRM_COLUMN, COLUMN_EDITING_RULES } from "../../constData/columnDrmt";
import DrmTable from "../tmt/DrmTable";
import { useExportExcelTableData } from "../../customHook/useExportExcelTableData";
import { useDailyTableOperations } from "../../customHook/useDailyTableOperations";
import { useFlipFlag } from "../../customHook/useFlipFlag";
import { useDailyDrmFilters } from "../../customHook/useDailyDrmFilters";
import { useDrmData } from "../../customHook/useDrmData";
import { useDialogStates } from "../../customHook/useDialogStates";
import { useDrmColumnManagement } from "../../customHook/useDrmColumnManagement";
import { DIALOG_TYPES } from "../../customHook/useDialogStates";
import { useDrmFilterUrlSync } from "../../customHook/useDrmFilterUrlSync";
import { snackBars, defaultFilter, exportToExcel, stringify, mapRateToChargeFields, modecreation, ADD_RECORD } from "../../helpers";
import CustomButton from "../common/CustomButton";
import DialogConformationMsg from "../tmt/DialogConformationMsg";
import { DRMT_EXCEL_LABEL } from "../../constData/excelColumn";
import { setDrmTableData, clearModifications } from "../../redux/reducers/drmTableDataSlice";
import { getColumView } from "../../redux/reducers/drmCustomColumnSlice";
import { setDrmTableFilter as setDailyTableFilter } from "../../redux/reducers/drmDailyTableFilterSlice";
import { drmtDailyFilterPayload } from "../../constData/filterRequestPayloadDrmtDaily";

import "./DailyMaintenance.sass";

const INITIAL_TABLE_STATE = {
  headers: [],
  toggleProps: null,
  density: "small",
  currentPage: 1,
};

const INITIAL_FILTER_DATA = {
  division: [],
  OCountry: [],
  DCountry: [],
};

const ActionButton = ({ datatestid, onClick, disabled, children, ...props }) => (
  <CustomButton
    datatestid={datatestid}
    className="bid_buttons"
    size="small"
    variant="outlined"
    onClick={onClick}
    disabled={disabled}
    btnType="nonary"
    {...props}
  >
    {children}
  </CustomButton>
);

const TABLE_TYPE = "drmDailyTableData";

function DailyMaintenance() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { dailyTableFilter } = useSelector((state) => state.drmDailyFilterPayload);
  const tableFilterRef = useRef(dailyTableFilter);
  tableFilterRef.current = dailyTableFilter;


  const { defaultColumns, customViews } = useSelector((state) => state.customColumn);
  const { drmDailyTableData: drmTableData, isLoading: isDrmDailyLoading } = useSelector((state) => state?.drmTableData || {});
  const { masterData } = useSelector((state) => state.masterData);
  const { userInfo } = useSelector((state)=>state.user);
  
  const selectedRows = drmTableData?.selectedRows || [];
  const { successMessage: updateSuccessMsg, isLoading: updateIsLoading} = useSelector((state) => state?.drmUpdates || {});
  const { successMessage: cancelSuccessMsg, isLoading: cancelIsLoading} = useSelector((state) => state?.drmDailyRecordCancel || {});
  const { successMessage: flipSuccessMsg, isLoading: flipIsLoading } = useSelector((state) => state?.drmFlipFlag || {});

  const [dailyTableState, setDailyTableState] = useState(INITIAL_TABLE_STATE);
  const [dailyFilterData, setDailyFilterData] = useState(INITIAL_FILTER_DATA);
  const [snackDuplicateDailyOpen, setDuplicateDailySnackOpen] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);

  const countryOptions = useMemo(
    () => masterData?.countries?.data || [],
    [masterData?.countries]
  );

   useDrmFilterUrlSync({
    scope: "daily",
    filterState: dailyTableFilter,
    dispatch,
    setFilterAction: setDailyTableFilter,
    uiState: dailyFilterData,
    setUiState: setDailyFilterData,
    initialUiState: INITIAL_FILTER_DATA,
    defaultFilterState: drmtDailyFilterPayload,
    countryOptions,
  });

  const { allColumnsWithStatus, toggleColumnVisibility, handleColumnReorder, orderedColumns: managedOrderedColumn } = useDrmColumnManagement(2, defaultColumns.daily, customViews.daily);

  const { submitState, setSubmitState, cancelState, setCancelState, flipFlagState, setFlipFlagState, closeDialog } = useDialogStates();

  const { handleDestinationCity, handleOriginCity, handleDivision } = useDailyDrmFilters(tableFilterRef, dispatch);
  const { handleFilterTableSearch, handleClearFilterTableSearch } = useDrmData(dispatch);
  const { apiSnackbar, apiSnackClose, handleSubmitConfirm, handleCancelConfirm } = useDailyTableOperations(dispatch, drmTableData);
  const { flipSnackbar, flipSnackClose, handleFlipFlagConfirm } = useFlipFlag(dispatch, drmTableData, 2);

  const { handleDownloadRecodes, handleClose, snackbarState } = useExportExcelTableData();

  const allowedDailyKeys = useMemo(() => DRMT_EXCEL_LABEL.map((n) => ({key: n.key, name: n.name})),[]);
  const handleDownDailyLoadTableRecords = useCallback(() => {
  if (selectedRows.length > 0) {
    const formatted = selectedRows.map((item) => {
      const original = item?.original;
      const record = { ...original, ...mapRateToChargeFields(original), mode: modecreation(original?.tariffId) };
      return Object.fromEntries(allowedDailyKeys.map(({key, name}) => [ name, stringify(record[key], key) || "" ]));
    });
    exportToExcel(formatted, "SelectedDailyExcelExport");
  } else {
    handleDownloadRecodes();
  }
}, [selectedRows, handleDownloadRecodes]);
 
  const handleDestinationCityWithState = useCallback(
    (value) => {
      handleDestinationCity(value);
      setDailyFilterData((prev) => ({ ...prev, DCountry: value }));
    },
    [handleDestinationCity]
  );

  const handleDivisionWithState = useCallback(
    (value) => {
      handleDivision(value);
      setDailyFilterData((prev) => ({ ...prev, division: value }));
    },
    [handleDivision]
  );

  const handleOriginCityWithState = useCallback(
    (value) => {
      handleOriginCity(value);
      setDailyFilterData((prev) => ({ ...prev, OCountry: value }));
    },
    [handleOriginCity]
  );

  const handleFilterSearch = useCallback(() => {
    handleFilterTableSearch(dailyTableState.currentPage, tableFilterRef.current, undefined, TABLE_TYPE);
  }, [handleFilterTableSearch, dailyTableState.currentPage]);

  const handleRefresh = useCallback(() => {
    handleClearFilterTableSearch(dailyTableState.currentPage, tableFilterRef.current, undefined, TABLE_TYPE);
  }, [handleClearFilterTableSearch, dailyTableState.currentPage]);

  const handlePageRefresh = useCallback(
    (page, type) => {
      handleFilterTableSearch(page, tableFilterRef.current, type, TABLE_TYPE);
    },
    [handleFilterTableSearch, dailyTableState.currentPage, dailyTableFilter, tableFilterRef]
  );

  const handleSubmitButtonClicked = useCallback(() => {
    setSubmitState({
      open: true,
      dialogTitle: t("submit_dialog_title"),
      dialogContentText: t("submit_dialog_content"),
      cancel: t("cancel"),
      confirm: t("submit"),
    });
  }, [setSubmitState]);

  const handleCancelButtonClicked = useCallback(() => {
    setCancelState({
      open: true,
      dialogTitle: t("cancel_dialog_title"),
      dialogContentText: t("cancel_dialog_content"),
      cancel: t("cancel"),
      confirm: t("submit"),
    });
  }, [setCancelState]);

    useEffect(() => {
      if (isDrmDailyLoading) {
        dispatch(clearModifications({ tableType: "drmDailyTableData" }));
      }
    }, [isDrmDailyLoading]);

    useEffect(() => {
      if (
        (!updateIsLoading && updateSuccessMsg != undefined) ||
        (!cancelIsLoading && cancelSuccessMsg != undefined) ||
        (!flipIsLoading && flipSuccessMsg != undefined)
      ) {
        dispatch(clearModifications({ tableType: "drmDailyTableData" }));
        handlePageRefresh(INITIAL_TABLE_STATE.currentPage, defaultFilter);
      }
    }, [updateSuccessMsg, updateIsLoading, cancelSuccessMsg, cancelIsLoading, flipSuccessMsg, flipIsLoading, handlePageRefresh]);
    
  useEffect(() => {
    const controller = new AbortController();
    const body = {
      viewCodes: [],
      viewNames: [],
      viewCategory: ["2"],
      isBookMark: [false, true],
      userCode: [userInfo?.userName],
    }
   dispatch(getColumView({ body , signal: controller.signal}))
     return () =>{
       controller.abort();
    }
  }, [userInfo])

  const handleSubmitConfirmWithClose = useCallback(() => {
    handleSubmitConfirm();
    closeDialog(DIALOG_TYPES.SUBMIT);
  }, [handleSubmitConfirm, closeDialog, defaultFilter]);

  const handleCancelConfirmWithClose = useCallback(() => {
    handleCancelConfirm();
    closeDialog(DIALOG_TYPES.CANCEL);
  }, [handleCancelConfirm, closeDialog, defaultFilter]);

  const handleFlipFlagButtonClicked = useCallback(() => {
    setFlipFlagState({
      open: true,
      dialogTitle: t("flip_flag_dialog_title"),
      dialogContentText: t("flip_flag_dialog_content"),
      cancel: t("cancel"),
      confirm: t("proceed"),
    });
  }, [setFlipFlagState, t]);

  const handleFlipFlagConfirmWithClose = useCallback(() => {
    handleFlipFlagConfirm();
    closeDialog(DIALOG_TYPES.FLIP_FLAG);
  }, [handleFlipFlagConfirm, closeDialog]);

  const createEmptyRecord = useCallback(() => {
    const emptyRecord = {};
    DRM_COLUMN.forEach((column) => {
      if (column.accessor) {
        emptyRecord[column.accessor] = ADD_RECORD[column.accessor];
      }
    });
    return { ...emptyRecord , status: -2 };
  }, []);

  const handleAddNewRecord = useCallback(() => {
    const newRecord = createEmptyRecord();
    dispatch(
      setDrmTableData({
        tableType: "drmDailyTableData",
        records: [newRecord],
      })
    );
    setDailyTableState((preValue) => ({
      ...preValue,
      currentPage: 1,
    }));
  }, [createEmptyRecord]);

  const getDuplicateFieldValue = (columnKey, existingValue) => {
      const newRecordRule = COLUMN_EDITING_RULES[columnKey]?.newRecord;
      if (!newRecordRule) {
        return existingValue;
      }

      if (newRecordRule.blank) {
        return null;
      }

      if (newRecordRule.copy === false) {
        return null;
      }

      return existingValue;
  };

  const handleDuplicateExistRecords = useCallback(() => {
    if (!selectedRows || selectedRows.length === 0) {
      setDuplicateDailySnackOpen(true);
      return;
    }

    const rowsToDuplicate = selectedRows.map(({ original }) => {
      const { sagaId, status, message,...rest } = original; //status, message

      const duplicated = Object.entries(rest).reduce((acc, [key, value]) => {
        acc[key] = getDuplicateFieldValue(key, value);
        return acc;
      }, {});

      return { ...duplicated, status: -3, message: t('duplicate_record') };
    });

    const newData = [...rowsToDuplicate];
    dispatch(
      setDrmTableData({
        tableType: "drmDailyTableData",
        records: newData,
      })
    );

    setDailyTableState((preValue) => ({
      ...preValue,
      currentPage: 1,
    }));
  }, [selectedRows]);

  const handleChangeLogHistory = () => {
    navigate("/dashboard/drm_tool/audit_history?process=2", { replace: true });
  };

  const handleColumnOrderDaily = useCallback((action, columnName) => {
    handleColumnReorder(columnName, action);
  }, [handleColumnReorder]);

  const columnOrder = useMemo(() => {
    const visibleColumnNames = managedOrderedColumn
      .filter(col => col.isVisible)
      .map(col => col.columnName);
    
    return visibleColumnNames
      .map(columnName => DRM_COLUMN.find(col => col.Header === columnName))
      .filter(Boolean);
  }, [managedOrderedColumn]);

  const tableData = useMemo(() => drmTableData?.drmMaintenanceDetails || [], [drmTableData?.drmMaintenanceDetails]);

  const hasSelectedRows = selectedRows.length > 0;
  const actionButtons = [
     {
      id: "cancel",
      dataTestId: "cancel",
      ariaLabel: "Cancel data",
      buttonText: t("cancel"),
      accessor: "cancel",
      onClick: handleCancelButtonClicked,
      disabled: !hasSelectedRows,
    },
    {
      id: "flipFlag",
      dataTestId: "flip_flag",
      ariaLabel: "Flip the flag",
      buttonText: t("flip_flag_btn"),
      accessor: "flipFlag",
      onClick: handleFlipFlagButtonClicked,
      disabled: !hasSelectedRows,
    },
    {
      id: "submit",
      dataTestId: "submit",
      ariaLabel: "Submit data",
      buttonText: t("submit"),
      accessor: "submit",
      onClick: handleSubmitButtonClicked,
      disabled: !hasSelectedRows,
    },
  ];
  const confirmationDialogs = [
    {
      id: "cancel",
      open: cancelState.open,
      handleClose: () => closeDialog(DIALOG_TYPES.CANCEL),
      handleConfirm: handleCancelConfirmWithClose,
      dialogTitle: cancelState.dialogTitle,
      dialogContentText: cancelState.dialogContentText,
      cancel: cancelState.cancel,
      confirm: cancelState.confirm,
    },
    {
      id: "submit",
      open: submitState.open,
      handleClose: () => closeDialog(DIALOG_TYPES.SUBMIT),
      handleConfirm: handleSubmitConfirmWithClose,
      dialogTitle: submitState.dialogTitle,
      dialogContentText: submitState.dialogContentText,
      cancel: submitState.cancel,
      confirm: submitState.confirm,
    },
    {
      id: "flipFlag",
      open: flipFlagState.open,
      handleClose: () => closeDialog(DIALOG_TYPES.FLIP_FLAG),
      handleConfirm: handleFlipFlagConfirmWithClose,
      dialogTitle: flipFlagState.dialogTitle,
      dialogContentText: flipFlagState.dialogContentText,
      cancel: flipFlagState.cancel,
      confirm: flipFlagState.confirm,
    },
  ];

  return (
    <Grid container className="drmtBidContainer">
      <Stack spacing={0.5} alignItems="flex-start" width="100%" px={2}>
        <Stack direction="row" justifyContent="space-between" width="100%">
          <Stack alignItems="flex-start" width="70%">
            <TableFilters
              pageType="daily_maintenance"
              filterData={dailyFilterData}
              handleDivision={handleDivisionWithState}
              handleOriginCity={handleOriginCityWithState}
              handleDestinationCity={handleDestinationCityWithState}
              handleFilterSearch={handleFilterSearch}
            />
          </Stack>
          <Stack alignItems="flex-end" width="29%">
            <TableActionButtons
              tableState={dailyTableState}
              setTableState={setDailyTableState}
              handleAddNewRecord={handleAddNewRecord}
              handleDuplicateExistRecords={handleDuplicateExistRecords}
              handleChangeLogHistory={handleChangeLogHistory}
              handleDownloadRecodes={handleDownDailyLoadTableRecords}
              handleColumnOrder={handleColumnOrderDaily}
              handleRefresh={handlePageRefresh}
              allColumnsWithStatus={allColumnsWithStatus}
              onToggleColumnVisibility={toggleColumnVisibility}
            />
          </Stack>
        </Stack>

        <DrmTable
          tableName="Daily"
          tableData={tableData}
          columnOrder={columnOrder}
          tableState={dailyTableState}
          editableRow={true}
          onPage={drmTableData?.onPage}
          totalPages={drmTableData?.totalPages}
          totalRecords={drmTableData?.totalRecords}
          isLoadingDrmTable={isDrmDailyLoading}
          setTableState={setDailyTableState}
          handleRefresh={handlePageRefresh}
          handleClear={handleRefresh}
        />

        <CustomSnackbar
          open={snackDuplicateDailyOpen}
          severity="warning"
          autoHideDuration={6000}
          datatestId={"no_rec_to_duplicate"}
          message={t("no_rec_to_duplicate")}
          handleClose={() => setDuplicateDailySnackOpen(false)}
        />

        <Stack direction="row" justifyContent="flex-end" spacing={1} width="100%" px={0}>
          {actionButtons.map((actionBtn) => (
            <ActionButton
              key={actionBtn.id}
              datatestid={actionBtn.dataTestId}
              onClick={actionBtn.onClick}
              disabled={actionBtn.disabled}
              ariaLabel={actionBtn.ariaLabel}
            >
              {actionBtn.buttonText}
            </ActionButton>
          ))}
        </Stack>

        {apiSnackbar.open && (
          <CustomSnackbar
            open={apiSnackbar.open}
            severity={apiSnackbar.severity}
            autoHideDuration={5000}
            datatestId="button-update"
            message={t(apiSnackbar.message)}
            handleClose={apiSnackClose}
          />
        )}

        {flipSnackbar.open && (
          <CustomSnackbar
            open={flipSnackbar.open}
            severity={flipSnackbar.severity}
            autoHideDuration={5000}
            datatestId="flip-flag-snackbar"
            message={flipSnackbar.message}
            handleClose={flipSnackClose}
          />
        )}

        {confirmationDialogs.map((dialog) => (
          <DialogConformationMsg
            key={dialog.id}
            open={dialog.open}
            handleClose={dialog.handleClose}
            handleConfirm={dialog.handleConfirm}
            dialogTitle={dialog.dialogTitle}
            dialogContentText={dialog.dialogContentText}
            cancel={dialog.cancel}
            confirm={dialog.confirm}
          />
        ))}

        <CustomSnackbar
          open={snackOpen}
          severity="error"
          autoHideDuration={5000}
          datatestId="invalid-update"
          message={t("no_rec_to_duplicate")}
          handleClose={() => setSnackOpen(false)}
        />

        {snackBars.map((snackbar) => {
          return (
            <CustomSnackbar
              key={snackbar.id}
              open={snackbarState?.[snackbar.handleAccessor]}
              severity={snackbar.severity}
              autoHideDuration={snackbar.autoHideDuration}
              datatestId={snackbar.datatestId}
              message={t(snackbar.message)}
              handleClose={() => handleClose(snackbar.handleAccessor)}
            />
          );
        })}
      </Stack>
    </Grid>
  );
}

export default DailyMaintenance;